import { useState, useEffect, useCallback } from 'react';
import { ChevronDown, Pencil as Edit2, Copy, Check, Loader2, Eye } from 'lucide-react';
import { VariablePill, parseContentWithVariables } from './VariablePill';
import { AIRegenerateButton } from './AIRegenerateButton';
import { VariableInsertPanel } from './VariableInsertPanel';
import { useVariableInsertion, type TemplateVariable } from '../../hooks/useVariableInsertion';
import type { TemplateStatus } from '../../services/templateService';

interface ModernScriptCardProps {
  title: string;
  content: string;
  placeholder?: string;
  onUpdate: (content: string) => void;
  onGenerateAI?: (tone: string, length: string, currentContent: string) => Promise<string>;
  onDirtyChange?: (isDirty: boolean) => void;
  maxLength?: number;
  defaultExpanded?: boolean;
  badge?: string;
  icon?: React.ReactNode;
  variables?: TemplateVariable[];
  templateStatus?: TemplateStatus;
  onApproveTemplate?: (content: string) => Promise<void>;
  approvingTemplate?: boolean;
  readOnly?: boolean;
}

export function ModernScriptCard({
  title,
  content,
  placeholder,
  onUpdate,
  onGenerateAI,
  onDirtyChange,
  maxLength,
  defaultExpanded = false,
  badge,
  icon,
  variables,
  templateStatus,
  onApproveTemplate,
  approvingTemplate,
  readOnly = false,
}: ModernScriptCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(content);
  const [copied, setCopied] = useState(false);

  const getContent = useCallback(() => editContent, [editContent]);
  const {
    showVariables,
    setShowVariables,
    insertedVar,
    textareaRef,
    trackCursor,
    handleInsertVariable,
  } = useVariableInsertion({
    onContentChange: setEditContent,
    getContent,
  });

  useEffect(() => {
    if (isEditing) {
      onDirtyChange?.(editContent !== content);
    }
  }, [isEditing, editContent, content]);

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSave = async () => {
    onUpdate(editContent);
    if (onApproveTemplate) {
      await onApproveTemplate(editContent);
    }
    setIsEditing(false);
    setShowVariables(false);
    onDirtyChange?.(false);
  };

  const handleCancel = () => {
    setEditContent(content);
    setIsEditing(false);
    setShowVariables(false);
    onDirtyChange?.(false);
  };

  const handleAcceptGenerated = (generatedContent: string) => {
    setEditContent(generatedContent);
    onUpdate(generatedContent);
  };

  const charCount = editContent.length;
  const isNearLimit = maxLength && charCount > maxLength * 0.9;
  const isOverLimit = maxLength && charCount > maxLength;

  const renderPreview = () => {
    if (!content) {
      return (
        <p className="text-gray-400 text-sm italic">
          {placeholder || 'No content configured yet'}
        </p>
      );
    }

    const lines = content.split('\n').slice(0, 3);
    const previewText = lines.join('\n');
    const hasMore = content.split('\n').length > 3;

    return (
      <div className="text-sm text-gray-700 leading-relaxed">
        {parseContentWithVariables(previewText).map((part, idx) => {
          if (part.type === 'variable') {
            return (
              <VariablePill
                key={idx}
                variable={part.content}
                type={part.variableType}
              />
            );
          }
          return <span key={idx}>{part.content}</span>;
        })}
        {hasMore && (
          <span className="text-gray-400 ml-1">...</span>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200">
      <div
        className="p-5 cursor-pointer"
        onClick={() => !isEditing && setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            <ChevronDown
              className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                isExpanded ? 'rotate-180' : ''
              }`}
            />
            {icon}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-gray-900 font-display">
                  {title}
                </h3>
                {templateStatus === 'approved' ? (
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                    Configured
                  </span>
                ) : templateStatus === 'draft' ? (
                  <span className="px-2.5 py-0.5 bg-[#05E0D1]/10 text-[#0bb5a5] text-xs font-semibold rounded-full flex items-center gap-1.5 border border-[#05E0D1]/25">
                    Draft - Review Pending
                    <Eye className="w-3 h-3" />
                  </span>
                ) : badge ? (
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                    {badge}
                  </span>
                ) : !content ? (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs font-medium rounded-full">
                    Not Configured
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
            {!readOnly && (
              <button
                onClick={() => {
                  setEditContent(content);
                  setIsEditing(true);
                  setIsExpanded(true);
                }}
                className="p-2 text-gray-400 hover:text-[#1875FF] hover:bg-[#1875FF]/10 rounded-lg transition-colors"
                title="Edit"
              >
                <Edit2 className="w-5 h-5" />
              </button>
            )}
            {!readOnly && onGenerateAI && (
              <AIRegenerateButton
                onRegenerate={onGenerateAI}
                onAccept={handleAcceptGenerated}
                currentContent={content}
              />
            )}
            <button
              onClick={handleCopy}
              disabled={!content}
              className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-30"
              title="Copy"
            >
              {copied ? (
                <Check className="w-5 h-5 text-green-600" />
              ) : (
                <Copy className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-5 pb-5 border-t border-gray-100">
          {isEditing && !readOnly ? (
            <div className="space-y-3 pt-4">
              <div>
                <textarea
                  ref={variables ? textareaRef : undefined}
                  value={editContent}
                  onChange={(e) => {
                    setEditContent(e.target.value);
                    if (variables) trackCursor();
                  }}
                  onSelect={variables ? () => trackCursor() : undefined}
                  onClick={variables ? () => trackCursor() : undefined}
                  onKeyUp={variables ? () => trackCursor() : undefined}
                  placeholder={placeholder}
                  rows={8}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] text-sm resize-none ${
                    isOverLimit ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {maxLength && (
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-xs text-gray-500">
                      <span
                        className={
                          isOverLimit
                            ? 'text-red-600 font-medium'
                            : isNearLimit
                            ? 'text-yellow-600 font-medium'
                            : ''
                        }
                      >
                        {charCount}
                      </span>
                      {' / '}
                      {maxLength} characters
                    </p>
                  </div>
                )}
              </div>

              {variables && (
                <VariableInsertPanel
                  variables={variables}
                  showVariables={showVariables}
                  onToggle={() => setShowVariables(!showVariables)}
                  onInsert={handleInsertVariable}
                  insertedVar={insertedVar}
                />
              )}

              <div className="flex justify-end gap-2">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isOverLimit || approvingTemplate}
                  className="px-4 py-2 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] text-white rounded-lg hover:shadow-lg hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 text-sm font-medium flex items-center gap-2"
                >
                  {approvingTemplate && <Loader2 className="w-4 h-4 animate-spin" />}
                  Save Changes
                </button>
              </div>
            </div>
          ) : (
            <div className="pt-4">
              <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {parseContentWithVariables(content).map((part, idx) => {
                  if (part.type === 'variable') {
                    return (
                      <VariablePill
                        key={idx}
                        variable={part.content}
                        type={part.variableType}
                      />
                    );
                  }
                  return <span key={idx}>{part.content}</span>;
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
